/* Phatstix — lazy 3D memory manager for <model-viewer>.
   Attaches a model when its host nears the viewport and DESTROYS it (freeing GPU + decoded-texture
   memory, and explicitly losing the WebGL context) when it scrolls away — so a phone only ever holds
   ONE model at a time. PLUS an idle render-pauser: model-viewer renders every frame while auto-rotate
   is on, even sitting idle in view, which crept iOS Safari to an OOM crash ("a problem repeatedly
   occurred") even when left alone. We stop auto-rotate after a few idle seconds (and on tab-hide), so
   the render loop settles to ~0 frames/sec; it resumes on scroll. Hard capability gate: no/old WebGL,
   reduced-motion, or no IntersectionObserver = never load (caller shows the static packshot).
   Self-heal: a device that actually loses its GL context is demoted to packshot on its next visit.
   Idempotent; safe to include twice. */
(function () {
  if (window.phMV) return;

  /* WebGL probe that reads MAX_TEXTURE_SIZE then FREES its own test context (don't leave one
     resident on a borderline device we're about to gate off). */
  function webglProbe() {
    var c = document.createElement('canvas'), gl = null, maxTex = 0;
    try {
      gl = c.getContext('webgl') || c.getContext('experimental-webgl');
      if (gl) { maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 0; }
    } catch (e) {}
    try { var lose = gl && gl.getExtension('WEBGL_lose_context'); if (lose) lose.loseContext(); } catch (e) {}
    try { c.width = c.height = 0; } catch (e) {}
    return { ok: !!gl, maxTex: maxTex };
  }

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches;
  var probe   = webglProbe();
  var cores   = navigator.hardwareConcurrency || 0;   // iOS exposes this; ≤2 ≈ iPhone 8/SE-1 era & older
  var dmem    = navigator.deviceMemory || 0;          // Android/desktop only (undefined → 0 on iOS)
  var lowCores   = cores > 0 && cores <= 2;
  var lowGPU     = probe.maxTex > 0 && probe.maxTex < 8192;   // old PowerVR (4096) vs A10+ (16384)
  var lowAndroid = dmem > 0 && dmem < 4;
  var forcedOff  = false;
  try { forcedOff = (location.search.indexOf('no3d') > -1) || (localStorage.getItem('ph_no3d') === '1'); } catch (e) {}

  var ok = !reduced && probe.ok && ('customElements' in window) && ('IntersectionObserver' in window)
        && !lowCores && !lowGPU && !lowAndroid && !forcedOff;

  /* Self-heal: if any device actually loses its WebGL context (it hit the GPU ceiling), remember it
     and serve the static packshot on the next load — turns a would-be crash into a graceful fallback. */
  window.addEventListener('webglcontextlost', function () { try { localStorage.setItem('ph_no3d', '1'); } catch (e) {} }, true);

  var moduleStarted = false;
  function loadModule() {
    if (moduleStarted) return;
    moduleStarted = true;
    var s = document.createElement('script');
    s.type = 'module';
    s.src = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js';
    document.head.appendChild(s);
  }

  /* Idle render-pauser. Stops auto-rotate after IDLE_MS of no scroll (and immediately on tab-hide),
     which lets model-viewer settle and quit its render loop → no continuous GPU/JS churn while the
     user sits on the page. Resumes on scroll. Wake is scroll/visibility only (NOT pointer) so it
     never fights the merch touch-drag-to-spin handler.

     OPT-OUT, added 30 Aug 2026: register({ idle: false }) turns the IDLE timer off for ONE
     registration, so that model keeps turning for as long as it is mounted. The merch PDP asks for
     this (Thom). It is cheap because the two other protections stay on for everybody:
       - register()'s IntersectionObserver still DESTROYS the model when it leaves the viewport, so
         "continuous" only ever means "continuous while on screen";
       - the tab-hide park below still fires, so a backgrounded tab still settles to ~0 fps.
     A caller that opts out MUST restore auto-rotate itself after any pointer interaction — with
     the timer off, nothing else will. assets/ph-gallery.js does this on pointerup.
     Everything that passes no `idle` keeps the 4s park unchanged: ph-hero and ph-merch are
     untouched by this edit. */
  var IDLE_MS = 4000;
  function idlePause(mv, idle) {
    if (!mv) return function () {};
    var ms = (idle === undefined || idle === null) ? IDLE_MS : idle;
    var never = (ms === false || ms === 0);
    var timer = null, paused = false;
    function park() { if (paused || !mv.isConnected) return; paused = true; try { mv.autoRotate = false; mv.removeAttribute('auto-rotate'); } catch (e) {} }
    function wake() { if (!mv.isConnected) return; if (paused) { paused = false; try { mv.setAttribute('auto-rotate', ''); mv.autoRotate = true; } catch (e) {} } arm(); }
    function arm() { clearTimeout(timer); if (!never) timer = setTimeout(park, ms); }
    var onVis = function () { if (document.hidden) { clearTimeout(timer); park(); } else { wake(); } };
    if (!never) window.addEventListener('scroll', wake, { passive: true });
    document.addEventListener('visibilitychange', onVis);
    if (mv.loaded) { arm(); } else { mv.addEventListener('load', arm, { once: true }); }  // spin from load, then park
    return function dispose() {
      clearTimeout(timer);
      window.removeEventListener('scroll', wake);
      document.removeEventListener('visibilitychange', onVis);
    };
  }

  /* Force-release the WebGL context on unmount — src='' + removeChild frees model assets but leaves
     iOS to GC the GPU context lazily; loseContext() drops it immediately so scrolling up/down never
     accumulates orphaned contexts. model-viewer recreates one cleanly on the next mount. */
  function releaseGL(mv) {
    try {
      var cv = mv.shadowRoot && mv.shadowRoot.querySelector('canvas');
      var gl = cv && (cv.getContext('webgl2') || cv.getContext('webgl'));
      var ext = gl && gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
    } catch (e) {}
  }

  /* register({ host, build, onAttach, onDetach, onUnsupported, rootMargin }) */
  function register(cfg) {
    var host = cfg.host;
    if (!host) return;
    if (!ok) { if (cfg.onUnsupported) cfg.onUnsupported(); return; }
    var current = null;
    /* Destroy the live model: stop idle listeners, release the GL context, drop assets, detach. */
    function destroy(viaDetach) {
      if (!current) return;
      if (current._phIdle) { try { current._phIdle(); } catch (_) {} }
      if (viaDetach && cfg.onDetach) cfg.onDetach(current);
      releaseGL(current);
      try { current.src = ''; } catch (_) {}              /* drop the model assets */
      if (current.parentNode) current.parentNode.removeChild(current);
      current = null;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          if (current) return;
          loadModule();
          current = cfg.build();
          current._phIdle = idlePause(current, cfg.idle);
          if (cfg.onAttach) cfg.onAttach(current);
        } else if (current) {
          destroy(true);
        }
      });
    }, { rootMargin: cfg.rootMargin || '300px 0px' });
    io.observe(host);

    /* Theme-editor lifecycle: when Shopify removes this section (shopify:section:unload on edit),
       disconnect the observer and destroy any live model — otherwise each edit leaks an
       IntersectionObserver, the idle scroll/visibility listeners and a WebGL context. The re-injected
       section runs its inline script again, which calls register() afresh for the new node, so re-init
       is automatic. Only wired in the editor (Shopify.designMode); the live storefront never re-renders
       sections, so visitors pay nothing. */
    if (window.Shopify && window.Shopify.designMode) {
      var onUnload = function (e) {
        if (!e.target || !e.target.contains || !e.target.contains(host)) return;
        try { io.disconnect(); } catch (_) {}
        destroy(false);
        document.removeEventListener('shopify:section:unload', onUnload);
      };
      document.addEventListener('shopify:section:unload', onUnload);
    }
  }

  window.phMV = { ok: ok, loadModule: loadModule, register: register, idlePause: idlePause };
})();
